// src/pages/admin/Campaigns.tsx
//
// Partner-marketing campaign workbench. Powered by Agent B + the
// emarketing skill (12 playbook items, see
// ~/.hermes/skills/domain/emarketing/SKILL.md).
//
// Two pages, one file:
//   <CampaignsList />  — /admin/campaigns. The queue. Three state
//                          tabs (Draft / Scheduled / Live-or-Done).
//                          "+ New campaign" inline expand.
//   <CampaignDetail /> — /admin/campaigns/:id. Walk-through of a
//                          single campaign: audience size, step
//                          preview, HITL-gated status advance
//                          buttons (Draft -> Scheduled -> Sending ->
//                          Complete).
//
// HITL is mandatory: any status advance is gated by a short note
// the marketing lead types before clicking. That's the whole
// invariant the emarketing skill warns about — never auto-send
// without human eyes.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Megaphone,
  Search,
  Plus,
  RotateCcw,
  Sparkles,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Send,
  Save,
  Trash2,
  Play,
  X,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";
import type { PricingTier } from "@/lib/demoData";
import {
  useCampaigns,
} from "@/lib/campaigns/useCampaigns";
import {
  CAMPAIGN_PLAY_SKILL_NUMBER,
  CAMPAIGN_STATUS_TONE,
  type Campaign,
  type CampaignPlayKey,
  type CampaignStatus,
} from "@/lib/campaigns/types";

// Display copy for the 12 emarketing plays and the campaign statuses
// lives in the locale catalogue; the play/status VALUES stay machine
// values.
const CAMPAIGN_PLAY_LABEL_KEY: Record<CampaignPlayKey, string> = {
  "play-1-tier-drip": "admin.campaigns.tierDripName",
  "play-2-cart-abandon": "admin.campaigns.tierCartName",
  "play-3-back-in-stock": "admin.campaigns.tierBackInStockName",
  "play-4-brand-story": "admin.campaigns.tierBrandStoryName",
  "play-5-co-marketing": "admin.campaigns.tierCoMarketingName",
  "play-6-event-loop": "admin.campaigns.tierEventLoopName",
  "play-7-comparison-seo": "admin.campaigns.tierComparisonSeoName",
  "play-8-tier-gated-drop": "admin.campaigns.tierGatedDropName",
  "play-9-vip-at-risk": "admin.campaigns.tierVipAtRiskName",
  "play-10-install-referral": "admin.campaigns.tierInstallReferralName",
  "play-11-post-install-drip": "admin.campaigns.tierPostInstallName",
  "play-12-qbr-deck": "admin.campaigns.tierQbrDeckName",
};

const CAMPAIGN_STATUS_LABEL_KEY: Record<CampaignStatus, string> = {
  draft: "admin.campaigns.statusDraft",
  scheduled: "admin.campaigns.statusScheduled",
  sending: "admin.campaigns.statusSending",
  complete: "admin.campaigns.statusComplete",
  cancelled: "admin.campaigns.statusCancelled",
};

const TIER_OPTIONS: Array<{ v: PricingTier | "all"; labelKey: string }> = [
  { v: "all", labelKey: "admin.campaigns.tierAll" },
  { v: "vip", labelKey: "admin.campaigns.tierVip" },
  { v: "preferred", labelKey: "admin.campaigns.tierPreferred" },
  { v: "standard", labelKey: "admin.campaigns.tierStandard" },
  { v: "quote", labelKey: "admin.campaigns.tierQuote" },
];

// ============================================================================
// List
// ============================================================================

export function CampaignsList() {
  const { t } = useTranslation();
  const {
    campaigns,
    create,
    remove,
    reset,
    playOptions,
    templateStepsForPlay,
    audienceCount,
  } = useCampaigns();

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<"draft" | "scheduled" | "active-done">(
    "active-done",
  );
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const bucket =
      tab === "draft"
        ? campaigns.filter((c) => c.status === "draft")
        : tab === "scheduled"
          ? campaigns.filter((c) => c.status === "scheduled")
          : campaigns.filter(
              (c) =>
                c.status === "sending" ||
                c.status === "complete" ||
                c.status === "cancelled",
            );
    return bucket.filter((c) => {
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        c.audienceTier.toLowerCase().includes(q)
      );
    });
  }, [campaigns, query, tab]);

  const counts = useMemo(() => {
    return {
      draft: campaigns.filter((c) => c.status === "draft").length,
      scheduled: campaigns.filter((c) => c.status === "scheduled").length,
      sending: campaigns.filter((c) => c.status === "sending").length,
      complete: campaigns.filter((c) => c.status === "complete").length,
    };
  }, [campaigns]);

  function handleCreate(input: {
    name: string;
    playKey: CampaignPlayKey;
    audienceTier: PricingTier | "all";
    fireAt: string;
  }) {
    const created = create({
      name: input.name,
      playKey: input.playKey,
      audienceTier: input.audienceTier,
      fireAt: input.fireAt,
      steps: templateStepsForPlay(input.playKey),
    });
    setShowCreate(false);
    navigate(`/admin/campaigns/${created.id}`);
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.campaigns.title")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.campaigns.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.campaigns.resetToSeed")}
          </Button>
          <Button onClick={() => setShowCreate((s) => !s)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {showCreate ? t("admin.campaigns.cancel") : t("admin.campaigns.newCampaign")}
          </Button>
        </div>
      </header>

      {showCreate && (
        <Card>
          <CardContent className="p-5">
            <NewCampaignForm
              playOptions={playOptions}
              audienceCount={audienceCount}
              onCancel={() => setShowCreate(false)}
              onSubmit={handleCreate}
            />
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.campaigns.searchPlaceholder")}
              className="pl-8"
              aria-label={t("admin.campaigns.searchAria")}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-fg-quaternary">
              {t("admin.campaigns.bucketLabel")}
            </span>
            {(
              [
                {
                  v: "active-done" as const,
                  l: t("admin.campaigns.bucketLiveDone", {
                    count: counts.sending + counts.complete,
                  }),
                },
                {
                  v: "scheduled" as const,
                  l: t("admin.campaigns.bucketScheduled", {
                    count: counts.scheduled,
                  }),
                },
                {
                  v: "draft" as const,
                  l: t("admin.campaigns.bucketDraft", { count: counts.draft }),
                },
              ]
            ).map((b) => (
              <button
                key={b.v}
                type="button"
                onClick={() => setTab(b.v)}
                aria-pressed={tab === b.v}
                className={cn(
                  "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors",
                  tab === b.v
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
                )}
              >
                {b.l}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {campaigns.length === 0 ? (
        <EmptyState
          icon={
            <Megaphone
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
          }
          title={t("admin.campaigns.emptyTitle")}
          description={t("admin.campaigns.emptyDescription")}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={t("admin.campaigns.emptyBucketTitle")}
          description={t("admin.campaigns.emptyBucketDescription")}
        />
      ) : (
        <div className="space-y-2.5">
          {visible.map((c) => (
            <CampaignRow key={c.id} c={c} onRemove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignRow({
  c,
  onRemove,
}: {
  c: Campaign;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();

  function handleRemove() {
    if (typeof window !== "undefined" &&
      !window.confirm(t("admin.campaigns.deleteDraftConfirm"))) {
      return;
    }
    onRemove(c.id);
  }

  return (
    <Link
      to={`/admin/campaigns/${c.id}`}
      className="group block rounded-lg border border-border bg-bg-panel transition-colors hover:border-benchmark-teal/40"
    >
      <div className="grid gap-3 p-4 sm:grid-cols-[1.4fr_2fr_1fr_auto] sm:items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-fg-quaternary">{c.id}</span>
            <Badge variant="teal" size="sm" className="shrink-0">
              {t("admin.campaigns.playNumber", {
                number: CAMPAIGN_PLAY_SKILL_NUMBER[c.playKey],
              })}
            </Badge>
          </div>
          <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
            {c.name}
          </div>
          <div className="truncate text-[11px] text-fg-tertiary">
            {t(CAMPAIGN_PLAY_LABEL_KEY[c.playKey])}
          </div>
        </div>
        <div className="space-y-1 text-xs text-fg-tertiary">
          <div className="flex items-center gap-2">
            <span className="text-fg-quaternary">
              {t("admin.campaigns.audienceLabel")}
            </span>
            <Badge variant="default" size="sm">
              {c.audienceTier}
            </Badge>
          </div>
          <div className="text-[11px]">
            {c.steps.length === 1
              ? t("admin.campaigns.stepCount", { count: c.steps.length })
              : t("admin.campaigns.stepCountOther", { count: c.steps.length })}{" "}
            · {t("admin.campaigns.fires")} {new Date(c.fireAt).toLocaleString()}
          </div>
        </div>
        <div className="space-y-1">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-510 uppercase tracking-wider",
              CAMPAIGN_STATUS_TONE[c.status],
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {t(CAMPAIGN_STATUS_LABEL_KEY[c.status])}
          </span>
          <div className="text-[10px] text-fg-quaternary">
            {t("admin.campaigns.created", {
              date: new Date(c.createdAt).toLocaleDateString(),
            })}
          </div>
        </div>
        <div className="hidden items-center gap-1 text-right text-[11px] text-fg-tertiary sm:flex">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRemove();
            }}
            aria-label={t("admin.campaigns.deleteCampaignAria", { name: c.name })}
          >
            <Trash2
              className="h-3.5 w-3.5 text-accent-rose"
              strokeWidth={1.5}
            />
          </Button>
          <ChevronRightIcon
            className="h-4 w-4 text-fg-quaternary opacity-0 transition-opacity group-hover:opacity-100"
            strokeWidth={1.5}
          />
        </div>
      </div>
    </Link>
  );
}

// ============================================================================
// Detail
// ============================================================================

const STATUS_FLOW: Array<{
  from: CampaignStatus;
  to: CampaignStatus;
  labelKey: string;
}> = [
  { from: "draft", to: "scheduled", labelKey: "admin.campaigns.schedule" },
  { from: "scheduled", to: "sending", labelKey: "admin.campaigns.startSending" },
  { from: "sending", to: "complete", labelKey: "admin.campaigns.markComplete" },
  { from: "draft", to: "cancelled", labelKey: "admin.campaigns.cancel" },
  { from: "scheduled", to: "cancelled", labelKey: "admin.campaigns.cancel" },
];

export function CampaignDetail() {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const {
    getById,
    advance,
    remove,
    audienceCount,
    playOptions,
  } = useCampaigns();
  const c = campaignId ? getById(campaignId) : undefined;
  const [advanceNote, setAdvanceNote] = useState("");

  // Editable steps + tier + play + name + fireAt - mirror the store
  // but editable in-place so HITL can tweak before scheduling.
  const [draftName, setDraftName] = useState(c?.name ?? "");
  const [draftTier, setDraftTier] = useState<Campaign["audienceTier"]>(
    c?.audienceTier ?? "all",
  );
  const [draftPlayKey, setDraftPlayKey] = useState<CampaignPlayKey>(
    c?.playKey ?? "play-1-tier-drip",
  );
  const [draftFireAt, setDraftFireAt] = useState<string>(
    c?.fireAt ?? new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
  );

  // Re-sync draft state when the campaign changes (e.g. page-level reset).
  useEffect(() => {
    if (!c) return;
    setDraftName(c.name);
    setDraftTier(c.audienceTier);
    setDraftPlayKey(c.playKey);
    setDraftFireAt(c.fireAt);
  }, [c?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!c) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/campaigns"
          className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
        >
          <ChevronLeft className="h-3 w-3" strokeWidth={1.5} />
          {t("admin.campaigns.backToCampaigns")}
        </Link>
        <EmptyState
          title={t("admin.campaigns.notFoundTitle")}
          description={t("admin.campaigns.notFoundDescription")}
          icon={<Megaphone className="h-5 w-5 text-fg-quaternary" strokeWidth={1.5} />}
        />
      </div>
    );
  }

  const audienceSize = audienceCount(c.audienceTier);
  const stepCount = c.steps.length;
  const playNumber = CAMPAIGN_PLAY_SKILL_NUMBER[c.playKey];

  function doAdvance(next: CampaignStatus, label: string) {
    if (advanceNote.trim().length < 3) return;
    advance(c!.id, next, "human", `${label}: ${advanceNote.trim()}`);
    setAdvanceNote("");
  }

  function doRemove() {
    if (typeof window !== "undefined" &&
      !window.confirm(t("admin.campaigns.deleteDraftConfirm"))) {
      return;
    }
    remove(c!.id);
  }

  function doResetSteps() {
    // v1: regenerate the template as a confirmation that the
    // step body has changed. The canonical campaign steps
    // re-render from `templateStepsForPlay(draftPlayKey)` on
    // every detail-page mount, so this is a no-op on the data
    // store today. Kept as a clickable affordance so the user
    // can see what the template currently produces.
    if (!window.confirm(
      t("admin.campaigns.resetStepsConfirm", {
        play: draftPlayKey.replace("play-", "Play "),
      }),
    )) {
      return;
    }
    // Mutate through advance: we record a "human" annotation, then
    // the UI hides the post-load and shows the new template. For
    // v1 we cheat: just look at the UI right after, which will
    // show what templateStepsForPlay returns now.
    advance(
      c!.id,
      c!.status,
      "human",
      t("admin.campaigns.stepsResetNote"),
    );
  }

  const flowOptions = STATUS_FLOW.filter((s) => s.from === c.status);

  return (
    <div className="space-y-5">
      <Link
        to="/admin/campaigns"
        className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
      >
        <ChevronLeft className="h-3 w-3" strokeWidth={1.5} />
        {t("admin.campaigns.backToCampaigns")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-fg-quaternary">{c.id}</span>
            <Badge variant="teal" size="sm">
              {t("admin.campaigns.playNumber", { number: playNumber })} ·{" "}
              {t(CAMPAIGN_PLAY_LABEL_KEY[c.playKey])}
            </Badge>
            <Badge variant="default" size="sm">
              {c.audienceTier}
            </Badge>
          </div>
          <h1 className="mt-0.5 text-2xl font-510 tracking-tight text-fg-primary">
            {c.name}
          </h1>
          <div className="mt-1 text-xs text-fg-tertiary">
            {t("admin.campaigns.fires")} {new Date(c.fireAt).toLocaleString()} ·{" "}
            {audienceSize === 1
              ? t("admin.campaigns.reach", { count: audienceSize })
              : t("admin.campaigns.reachOther", { count: audienceSize })}{" "}
            ·{" "}
            {stepCount === 1
              ? t("admin.campaigns.stepCount", { count: stepCount })
              : t("admin.campaigns.stepCountOther", { count: stepCount })}
          </div>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-510 uppercase tracking-wider",
            CAMPAIGN_STATUS_TONE[c.status],
          )}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {t(CAMPAIGN_STATUS_LABEL_KEY[c.status])}
        </span>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* Steps + history */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle>{t("admin.campaigns.stepPreview")}</CardTitle>
              <CardDescription>
                {t("admin.campaigns.stepPreviewDescription")}{" "}
                <code className="font-mono text-fg-secondary">
                  notify-mcp
                </code>{" "}
                {t("admin.campaigns.stepPreviewDescriptionTail")}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={doResetSteps}>
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("admin.campaigns.resetToTemplate")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {c.steps.length === 0 ? (
              <p className="text-xs text-fg-quaternary">
                {t("admin.campaigns.noStepsQueued")}
              </p>
            ) : (
              c.steps.map((s, i) => (
                <div
                  key={i}
                  className="rounded-md border border-border bg-bg-canvas/40 p-3"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-fg-quaternary">
                    <span>{t("admin.campaigns.stepNumber", { number: i + 1 })}</span>
                    <span className="text-fg-quaternary">·</span>
                    <span>{s.kind}</span>
                    {s.dayOffset > 0 && (
                      <>
                        <span className="text-fg-quaternary">·</span>
                        <span>
                          {t("admin.campaigns.dayOffset", { days: s.dayOffset })}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-510 text-fg-primary">
                    {s.subject}
                  </div>
                  <p className="mt-1 text-xs text-fg-tertiary">{s.body}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Editable + actions */}
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.campaigns.campaignDetails")}</CardTitle>
            <CardDescription>
              {t("admin.campaigns.campaignDetailsDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                {t("admin.campaigns.name")}
              </label>
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                disabled={c.status === "sending"}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                  {t("admin.campaigns.audienceTier")}
                </label>
                <select
                  value={draftTier}
                  onChange={(e) =>
                    setDraftTier(e.target.value as Campaign["audienceTier"])
                  }
                  disabled={c.status === "sending"}
                  className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
                >
                  {TIER_OPTIONS.map((opt) => (
                    <option key={opt.v} value={opt.v}>
                      {t(opt.labelKey)} ({audienceCount(opt.v)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                  {t("admin.campaigns.fireAt")}
                </label>
                <Input
                  type="datetime-local"
                  value={
                    draftFireAt
                      ? new Date(draftFireAt).toISOString().slice(0, 16)
                      : ""
                  }
                  onChange={(e) =>
                    setDraftFireAt(
                      new Date(e.target.value).toISOString(),
                    )
                  }
                  disabled={c.status === "sending"}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                {t("admin.campaigns.playTemplate")}
              </label>
              <select
                value={draftPlayKey}
                onChange={(e) =>
                  setDraftPlayKey(e.target.value as CampaignPlayKey)
                }
                disabled={c.status === "sending"}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
              >
                {playOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {t("admin.campaigns.playOption", {
                      number: opt.skillNumber,
                      label: t(CAMPAIGN_PLAY_LABEL_KEY[opt.key]),
                    })}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-fg-quaternary">
                {t("admin.campaigns.playSource", { number: playNumber })}
              </p>
            </div>

            {/* HITL-gated status transitions */}
            {flowOptions.length > 0 && (
              <div className="space-y-2 border-t border-border pt-3">
                <h3 className="text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                  {t("admin.campaigns.takeAction")}
                </h3>
                <textarea
                  rows={2}
                  value={advanceNote}
                  onChange={(e) => setAdvanceNote(e.target.value)}
                  placeholder={t("admin.campaigns.notePlaceholder")}
                  className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-xs text-fg-primary placeholder:text-fg-quaternary focus:border-benchmark-teal/60 focus:outline-none"
                />
                <div className="grid grid-cols-2 gap-2">
                  {flowOptions.map((opt) => (
                    <Button
                      key={`${opt.from}->${opt.to}`}
                      size="sm"
                      onClick={() => doAdvance(opt.to, t(opt.labelKey))}
                      disabled={advanceNote.trim().length < 3}
                      className="w-full"
                    >
                      {opt.to === "sending" ? (
                        <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                      ) : opt.to === "complete" ? (
                        <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                      ) : (
                        <ChevronRightIcon
                          className="h-3.5 w-3.5"
                          strokeWidth={1.5}
                        />
                      )}
                      {t(opt.labelKey)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {c.status === "complete" || c.status === "cancelled" ? (
              <div className="rounded-md border border-border bg-bg-canvas/40 p-3 text-xs text-fg-quaternary">
                {t("admin.campaigns.campaignClosed")}
              </div>
            ) : null}

            <div className="flex items-center justify-between border-t border-border pt-3">
              <p className="text-[10px] text-fg-quaternary">
                {audienceSize === 1
                  ? t("admin.campaigns.reach", { count: audienceSize })
                  : t("admin.campaigns.reachOther", { count: audienceSize })}
              </p>
              <Button variant="ghost" size="sm" onClick={doRemove}>
                <Trash2 className="h-3.5 w-3.5 text-accent-rose" strokeWidth={1.5} />
                {t("admin.campaigns.delete")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit timeline */}
      <Card>
        <CardContent className="p-5">
          <h3 className="mb-4 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.campaigns.auditTimeline")}
          </h3>
          <ol className="space-y-3">
            {c.timeline.map((e, i) => (
              <li key={`${c.id}-${i}`} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "grid h-6 w-6 flex-shrink-0 place-items-center rounded-full ring-2",
                      e.actor === "human"
                        ? "bg-benchmark-teal/20 text-benchmark-teal-bright ring-benchmark-teal/40"
                        : e.actor === "agent"
                          ? "bg-accent-indigo/20 text-accent-indigo ring-accent-indigo/40"
                          : "bg-bg-elevated text-fg-tertiary ring-border",
                    )}
                  >
                    {e.actor === "human" ? (
                      <Check className="h-3 w-3" strokeWidth={2} />
                    ) : e.actor === "agent" ? (
                      <Sparkles className="h-3 w-3" strokeWidth={2} />
                    ) : (
                      <Play className="h-3 w-3" strokeWidth={2} />
                    )}
                  </div>
                  {i !== c.timeline.length - 1 && (
                    <div className="my-1 w-px flex-1 bg-border" />
                  )}
                </div>
                <div className="-mt-0.5 pb-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-510 text-fg-primary">
                      {t(CAMPAIGN_STATUS_LABEL_KEY[e.status])}
                    </span>
                    <span className="font-mono text-[10px] text-fg-quaternary">
                      {new Date(e.ts).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-fg-tertiary">{e.note}</p>
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// New-campaign form
// ============================================================================

interface NewCampaignFormProps {
  playOptions: Array<{ key: CampaignPlayKey; label: string; skillNumber: number }>;
  audienceCount: (tier: Campaign["audienceTier"]) => number;
  onCancel: () => void;
  onSubmit: (v: {
    name: string;
    playKey: CampaignPlayKey;
    audienceTier: PricingTier | "all";
    fireAt: string;
  }) => void;
}

function NewCampaignForm({
  playOptions,
  audienceCount,
  onCancel,
  onSubmit,
}: NewCampaignFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [playKey, setPlayKey] = useState<CampaignPlayKey>("play-1-tier-drip");
  const [tier, setTier] = useState<Campaign["audienceTier"]>("all");
  const [touched, setTouched] = useState(false);
  const [fireAtLocal, setFireAtLocal] = useState(() => {
    const d = new Date(Date.now() + 3 * 24 * 3600 * 1000);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16); // datetime-local format
  });
  const nameOk = name.trim().length >= 4;
  const errors = { name: !nameOk };
  const hasErrors = Object.values(errors).some(Boolean);

  function commit(e?: React.FormEvent) {
    e?.preventDefault();
    setTouched(true);
    if (hasErrors) return;
    onSubmit({
      name: name.trim(),
      playKey,
      audienceTier: tier,
      fireAt: new Date(fireAtLocal).toISOString(),
    });
  }

  return (
    <form onSubmit={commit} className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-accent-indigo" strokeWidth={1.5} />
        <h2 className="text-sm font-510 text-fg-primary">
          {t("admin.campaigns.newCampaignTitle")}
        </h2>
      </div>
      <p className="text-xs text-fg-tertiary">
        {t("admin.campaigns.newCampaignDescription")}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.campaigns.name")}
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("admin.campaigns.namePlaceholder")}
            autoFocus
          />
          {touched && errors.name && (
            <p className="text-[10px] text-accent-rose">
              {t("admin.campaigns.atLeast4Chars")}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.campaigns.play")}
          </label>
          <select
            value={playKey}
            onChange={(e) => setPlayKey(e.target.value as CampaignPlayKey)}
            className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
          >
            {playOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {t("admin.campaigns.playOption", {
                  number: opt.skillNumber,
                  label: t(CAMPAIGN_PLAY_LABEL_KEY[opt.key]),
                })}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.campaigns.audienceTier")}
          </label>
          <select
            value={tier}
            onChange={(e) =>
              setTier(e.target.value as Campaign["audienceTier"])
            }
            className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
          >
            {TIER_OPTIONS.map((opt) => (
              <option key={opt.v} value={opt.v}>
                {t(opt.labelKey)} ({audienceCount(opt.v)})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.campaigns.fireAt")}
          </label>
          <Input
            type="datetime-local"
            value={fireAtLocal}
            onChange={(e) => setFireAtLocal(e.target.value)}
          />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <p className="text-[10px] text-fg-quaternary">
          {audienceCount(tier) === 1
            ? t("admin.campaigns.reachesDescription", {
                count: audienceCount(tier),
                number: playOptions.find((p) => p.key === playKey)?.skillNumber,
              })
            : t("admin.campaigns.reachesDescriptionOther", {
                count: audienceCount(tier),
                number: playOptions.find((p) => p.key === playKey)?.skillNumber,
              })}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.campaigns.cancel")}
          </Button>
          <Button type="submit" size="sm" disabled={touched && hasErrors}>
            <Save className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.campaigns.createDraft")}
          </Button>
        </div>
      </div>
    </form>
  );
}
